import BaseModal from 'components/UI/BaseModal';
import React, { FC } from 'react';
import { Trans } from 'react-i18next';
import InfoBlock from 'components/UI/InfoBlock';
import FileDropzone from 'components/UI/FileDropzone';
import Loader from 'components/UI/Loader';
import { ReactComponent as CheckSvg } from 'assets/check-circle.svg';
import Accordion from 'components/UI/Accordion';
import { FiInfo } from 'react-icons/fi';
import { ReactComponent as ArchiveSvg } from 'assets/archiveBg.svg';
import cn from 'classnames';
import { fetchMassCreativeCreate } from 'domains/creative/model/actions';
import { checkedLocalization } from 'utils/checkedLocalization';
import css from './styles.module.scss';
import {
  IPackageUploadModalProps,
  TInfoBlockConfigValue,
  TUploadStep,
  UPLOADSTAGELIST,
} from './types';
import UploadItem from './components/UploadItem';
import { usePackageUploadModal } from './hooks/usePackageUploadModal';

const PackageUploadModal: FC<IPackageUploadModalProps> = ({
  onCloseModal,
  xxhash,
  task_id,
  clickOutside = false,
}) => {
  const {
    t,
    dispatch,
    history,
    step,
    isLoading,
    uploadDispatch,
    files,
    setFiles,
    openId,
    task,
    isTaskLoading,
    textError,
    setTextError,
    creativeTypes,
    handleToggle,
    location,
  } = usePackageUploadModal({ xxhash, task_id });

  // Конфигурация отображения информационного блока для каждого этапа
  const infoBlockConfig: Partial<Record<TUploadStep, TInfoBlockConfigValue>> = {
    [UPLOADSTAGELIST.Intro]: {
      i18nKey: 'upload_creo_html',
    },
    [UPLOADSTAGELIST.ResultSuccess]: {
      i18nKey: 'upload_creo_success',
      color: 'GREEN',
      customIcon: <CheckSvg />,
      values: {
        /* Сюда надо передать, сколько файлов распозналось */
        total: task?.successful_creative?.count || 0,
      },
    },
    [UPLOADSTAGELIST.ResultPartial]: {
      i18nKey: 'upload_creo_partial',
      color: 'YELLOW',
      values: {
        /* Сюда надо передать, сколько файлов распозналось */
        correct: task?.successful_creative?.count || 0,
        /* Сюда надо передать, сколько всего файлов было */
        total: task?.creative_count || 0,
      },
    },
    [UPLOADSTAGELIST.ResultError]: {
      i18nKey: 'upload_creo_error',
      color: 'RED',
      values: {
        /* Сюда надо передать, сколько файлов с ошибкой получилось */
        total: task?.error_creative?.count || 0,
      },
    },
  };

  // Получаем конфигурацию Инфо блока для текущего шага
  const infoBlockRender = infoBlockConfig[step];
  const link = xxhash || task?.campaign_xxhashes;

  return (
    <BaseModal
      onClose={onCloseModal}
      isOpen
      title={t(`creative.package_upload_title`)}
      actions={null}
      isLoading={isTaskLoading}
      clickOutside={clickOutside}
    >
      <div className={css.body}>
        {infoBlockRender && (
          <InfoBlock
            color={infoBlockRender.color}
            text={
              <Trans
                i18nKey={infoBlockRender.i18nKey}
                values={infoBlockRender.values}
              />
            }
            customIcon={infoBlockRender.customIcon}
          />
        )}
        <div
          className={cn(css.fileDropzone, {
            _isFixed: [
              'INTRO',
              'UPLOAD',
              'PROCESSING',
              'ARCHIVE_ERROR',
            ].includes(step),
          })}
        >
          {/** 1. Шаг где мы можем перетащить файл в зон распознавания */}
          {step === UPLOADSTAGELIST.Intro && xxhash && (
            <FileDropzone
              multiple={false}
              onFilesChange={(newFiles) => {
                const updatedFiles = [...files, ...newFiles];
                setFiles(updatedFiles);

                dispatch(
                  fetchMassCreativeCreate({
                    type: 'massCreativeAdd',
                    mass_creative_type: 'weborama',
                    campaign_xxhash: xxhash,
                    files: updatedFiles,
                    callbacks: {
                      start: () => uploadDispatch({ type: 'UPLOAD_START' }),
                      processing: () => uploadDispatch({ type: 'PROCESSING' }),
                      error: (value: string) => {
                        setTextError(value);
                        uploadDispatch({ type: 'ARCHIVE_ERROR' });
                      },
                    },
                  }),
                );
              }}
            />
          )}
          {/** 2. Шаг где мы ждем пока распознаются файлы */}
          {isLoading && (
            <div className={css.uploading}>
              <Loader />
              <Trans
                i18nKey="creative.eridToken.executingRequest"
                components={{ br: <br /> }}
              />
            </div>
          )}
          {/** 3. Шаг где показываем что распозналось, а что нет */}
          <div className={css.list}>
            {/** Список с зелеными успешными загрузками */}
            {(step === UPLOADSTAGELIST.ResultSuccess ||
              step === UPLOADSTAGELIST.ResultPartial) && (
              <Accordion
                className={css.list_success}
                customHeaderIcon={<CheckSvg className={css.list_icon} />}
                title={
                  <Trans
                    i18nKey="successfully_processed"
                    values={{
                      /* Сюда надо передать, сколько файлов распозналось */
                      count: task?.successful_creative.count || 0,
                    }}
                  />
                }
                isOpen={openId === 'green'}
                onClick={() => handleToggle('green')}
              >
                {task?.successful_creative.creative_info.map((item, index) => {
                  const isCreativeTypesLoaded = Boolean(creativeTypes?.length);
                  const iconType = creativeTypes?.find(
                    (type) => type.key === item.type,
                  );
                  const icon = isCreativeTypesLoaded
                    ? iconType?.icon ?? null
                    : null;

                  return (
                    <UploadItem
                      key={item.xxhash}
                      number={index + 1}
                      name={item.title}
                      icon={icon}
                      isIconLoaded={isCreativeTypesLoaded}
                    />
                  );
                })}
              </Accordion>
            )}
            {/** Список с красными ошибками */}
            {(step === UPLOADSTAGELIST.ResultPartial ||
              step === UPLOADSTAGELIST.ResultError) &&
              task && (
                <Accordion
                  className={css.list_error}
                  customHeaderIcon={<FiInfo className={css.list_icon} />}
                  title={
                    <Trans
                      i18nKey="error_processed"
                      values={{
                        /* Сюда надо передать, сколько файлов распозналось */
                        count: task?.error_creative.count || 0,
                      }}
                    />
                  }
                  isOpen={openId === 'red'}
                  onClick={() => handleToggle('red')}
                >
                  {task.error_creative.error_info.map((item, index) => (
                    <UploadItem
                      key={item.file_name}
                      number={index + 1}
                      name={item.file_name}
                      error={item.error_message}
                      icon={null}
                      isIconLoaded={false}
                    />
                  ))}
                </Accordion>
              )}
          </div>
        </div>
        {['RESULT_SUCCESS', 'RESULT_PARTIAL'].includes(step) && (
          <button
            type="button"
            className={css.link_toCamp}
            onClick={() => {
              if (!link) return;
              const currentLocation = location.pathname;
              const pushHistory = `/main/campaign/${link}/creatives`;

              if (currentLocation !== pushHistory) {
                history.push(pushHistory);
              }

              onCloseModal();
            }}
          >
            {t(`showInAdCampaign`)}
          </button>
        )}
        {/** Шаг где мы говорим, что архив в обработке */}
        {['PROCESSING', 'ARCHIVE_ERROR'].includes(step) && (
          <div
            className={cn(css.absolute, {
              _isError: step === UPLOADSTAGELIST.ArchiveError,
            })}
          >
            <ArchiveSvg className={css.absolute_img} />
            <div className={css.absolute_info}>
              <strong className={css.absolute_title}>
                {step === UPLOADSTAGELIST.Processing
                  ? t(`archiveProcessing`)
                  : checkedLocalization(textError, textError)}
              </strong>
              {textError === '' && (
                <span className={css.absolute_text}>
                  {t(`archiveProcessingDescription`)}
                </span>
              )}
            </div>
            <button
              className={css.absolute_btn}
              type="button"
              onClick={() =>
                ['ARCHIVE_ERROR'].includes(step) &&
                location.pathname.includes('control_panel/tasks')
                  ? onCloseModal
                  : window.open(
                      '/control_panel/tasks',
                      '_blank',
                      'noopener,noreferrer',
                    )
              }
            >
              {t(`viewTaskList`)}
            </button>
            <button
              className={css.absolute_btn}
              type="button"
              onClick={onCloseModal}
            >
              {t(`close_btn`)}
            </button>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default PackageUploadModal;
